import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FiX, FiAlertCircle, FiCheckCircle, FiMoreHorizontal, FiMapPin } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import apiClient, { withApiPath } from '../../utils/apiClient';
import Button from '../ui/Button';
import LocationSelector from '../quotes/LocationSelector';
import styles from './QuickCreateShipmentModal.module.css';

const managedByOptions = [
  { label: 'FreightLogic managed', value: 'FREIGHTLOGIC' },
  { label: 'Managed by this account', value: 'ACCOUNT_SELF' }
];

const equipmentOptions = [
  'Dry Van',
  'Reefer',
  'Flatbed',
  'Step Deck',
  'Conestoga',
  'RGN',
  'Lowboy',
  'Box Truck',
  'Sprinter Van'
];

const buildLocationPayload = (location, date) => {
  const payload = { date };
  if (!location) {
    return payload;
  }

  if (location.id) {
    return { ...payload, location_id: location.id };
  }

  return {
    ...payload,
    location: {
      name: location.name || location.company_name || '',
      street: location.street || location.address || '',
      city: location.city || '',
      state: location.state || '',
      zip: location.zip || location.zip_code || ''
    }
  };
};

const getLocationName = (location) => location?.name || location?.company_name || 'Unnamed location';

const getLocationAddress = (location) => {
  if (!location) return '';
  const parts = [location.street || location.address, location.city, location.state, location.zip || location.zip_code]
    .filter(Boolean)
    .join(', ');
  return parts;
};

const QuickCreateShipmentModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const closeButtonRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const [managedBy, setManagedBy] = useState('FREIGHTLOGIC');
  const shipmentType = 'FTL';
  const [equipmentType, setEquipmentType] = useState('Dry Van');
  const [pickupLocation, setPickupLocation] = useState(null);
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [pickupDate, setPickupDate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [clientErrors, setClientErrors] = useState({});
  const [serverErrors, setServerErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [pickupSelectorSignal, setPickupSelectorSignal] = useState(null);
  const [deliverySelectorSignal, setDeliverySelectorSignal] = useState(null);

  const fieldErrors = useMemo(
    () => ({ ...clientErrors, ...serverErrors }),
    [clientErrors, serverErrors]
  );

  const resetFormState = () => {
    setClientErrors({});
    setServerErrors({});
    setGeneralError('');
    setIsSuccess(false);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetFormState();
    setAttemptedSubmit(false);
    onClose();
  };

  const computeClientErrors = () => {
    const clientErrors = {};

    if (!managedBy) {
      clientErrors.managed_by = ['Managed by is required'];
    }

    if (!equipmentType) {
      clientErrors.equipment_type = ['Equipment type is required'];
    }

    if (!pickupLocation) {
      clientErrors.pickup = ['Pickup location is required'];
    }

    if (!pickupDate) {
      clientErrors['pickup.date'] = ['Pickup date is required'];
    }

    if (!deliveryLocation) {
      clientErrors.delivery = ['Delivery location is required'];
    }

    if (!deliveryDate) {
      clientErrors['delivery.date'] = ['Delivery date is required'];
    }

    if (pickupDate && deliveryDate && new Date(deliveryDate) < new Date(pickupDate)) {
      clientErrors['delivery.date'] = ['Delivery date cannot be before pickup date'];
    }

    return clientErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    resetFormState();
    setAttemptedSubmit(true);

    const clientErrors = computeClientErrors();
    if (Object.keys(clientErrors).length > 0) {
      setClientErrors(clientErrors);
      setServerErrors({});
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        managed_by: managedBy,
        equipment_type: equipmentType,
        pickup: buildLocationPayload(pickupLocation, pickupDate),
        delivery: buildLocationPayload(deliveryLocation, deliveryDate)
      };

      const response = await apiClient.post(withApiPath('/loads/quick-create/'), payload);
      setIsSuccess(true);
      const newLoadId = response?.data?.id;
      if (newLoadId) {
        navigate(`/loads/${newLoadId}`);
      }
      handleClose();
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data) {
        setServerErrors(error.response.data);
      } else if (error.response?.status === 403) {
        setGeneralError('You do not have permission to create a shipment with the selected locations.');
      } else {
        setGeneralError('Unable to create shipment. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFieldError = (key) => {
    if (!fieldErrors[key]) return null;
    const message = Array.isArray(fieldErrors[key])
      ? fieldErrors[key].join(', ')
      : typeof fieldErrors[key] === 'object'
        ? Object.values(fieldErrors[key] || {}).flat().join(', ')
        : fieldErrors[key];
    return (
      <div className={styles.fieldError}>
        {message}
      </div>
    );
  };

  useEffect(() => {
    if (attemptedSubmit || Object.values(touched).some(Boolean)) {
      setClientErrors(computeClientErrors());
    }
  }, [attemptedSubmit, touched, managedBy, equipmentType, pickupLocation, pickupDate, deliveryLocation, deliveryDate]);

  const isFormValid = useMemo(() => {
    const validation = computeClientErrors();
    return Object.keys(validation).length === 0;
  }, [managedBy, equipmentType, pickupLocation, pickupDate, deliveryLocation, deliveryDate]);

  useEffect(() => {
    if (!isOpen) return undefined;
    previouslyFocusedRef.current = document.activeElement;
    closeButtonRef.current?.focus();
    return () => previouslyFocusedRef.current?.focus?.();
  }, [isOpen]);

  const shouldShowError = (key) => attemptedSubmit || touched[key];

  const locationField = (idPrefix, label, value, setValue, setSignal, errorKey) => {
    const name = getLocationName(value);
    const address = getLocationAddress(value);
    const hasError = fieldErrors[errorKey] && shouldShowError(errorKey);
    return (
      <div className={[styles.formField, hasError ? styles.invalid : ''].filter(Boolean).join(' ')}>
        <div className={styles.labelRow}>
          <span>{label}</span>
        </div>
        <button
          type="button"
          className={styles.locationInput}
          onClick={() => {
            setSignal(Date.now());
            setTouched((prev) => ({ ...prev, [errorKey]: true }));
          }}
          aria-label={`Select ${label.toLowerCase()}`}
        >
          <FiMapPin className={styles.locationIcon} />
          <div className={styles.locationBody}>
            <div className={styles.locationPrimary}>{value ? name : 'Select a saved location'}</div>
            <div className={styles.locationSecondary}>
              {value ? address || 'Address pending' : 'Search saved facilities or add a new one.'}
            </div>
          </div>
          <FiMoreHorizontal className={styles.locationActionIcon} />
        </button>
        {hasError && renderFieldError(errorKey)}
        <LocationSelector
          label={label}
          value={value}
          onChange={(next) => {
            setValue(next);
            setTouched((prev) => ({ ...prev, [errorKey]: true }));
          }}
          renderTrigger={false}
          openSignal={idPrefix === 'pickup' ? pickupSelectorSignal : deliverySelectorSignal}
        />
      </div>
    );
  };

  return isOpen ? (
    <div className={styles.backdrop}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="quick-create-title" aria-describedby="quick-create-description">
        <header className={styles.header}>
          <div>
            <h2 id="quick-create-title">New shipment</h2>
            <p id="quick-create-description" className={styles.subtitle}>Create the load now. Complete operational details in the workspace.</p>
          </div>
          <button ref={closeButtonRef} type="button" className={styles.closeButton} onClick={handleClose} aria-label="Close new shipment">
            <FiX />
          </button>
        </header>

        {generalError && (
          <div className={styles.inlineAlert}>
            <FiAlertCircle />
            <span>{generalError}</span>
          </div>
        )}

        {isSuccess && (
          <div className={styles.inlineSuccess}>
            <FiCheckCircle />
            <span>Shipment created. Redirecting to workspace…</span>
          </div>
        )}

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.configGrid}>
            <label className={[styles.formField, styles.topRowField, fieldErrors.managed_by && styles.invalid].filter(Boolean).join(' ')}>
              <span>Managed by</span>
              <select
                className={styles.topRowControl}
                value={managedBy}
                onChange={(event) => {
                  setManagedBy(event.target.value);
                  setTouched((prev) => ({ ...prev, managed_by: true }));
                }}
                onBlur={() => setTouched((prev) => ({ ...prev, managed_by: true }))}
              >
                {managedByOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {shouldShowError('managed_by') && renderFieldError('managed_by')}
            </label>

            <label className={[styles.formField, styles.topRowField].join(' ')}>
              <span>Shipment type</span>
              <select className={styles.topRowControl} value={shipmentType} disabled>
                <option value="FTL">Full Truckload (FTL)</option>
              </select>
            </label>

            <label className={[styles.formField, styles.topRowField, fieldErrors.equipment_type && styles.invalid].filter(Boolean).join(' ')}>
              <span>Equipment type</span>
              <select
                className={styles.topRowControl}
                value={equipmentType}
                onChange={(event) => {
                  setEquipmentType(event.target.value);
                  setTouched((prev) => ({ ...prev, equipment_type: true }));
                }}
                onBlur={() => setTouched((prev) => ({ ...prev, equipment_type: true }))}
              >
                {equipmentOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {shouldShowError('equipment_type') && renderFieldError('equipment_type')}
            </label>
          </div>

          <div className={styles.sectionHeading}>Route</div>
          <div className={styles.routeSection}>
            <div className={styles.routeGrid}>
              <div className={styles.routeRow}>
                {locationField('pickup', 'Pickup', pickupLocation, setPickupLocation, setPickupSelectorSignal, 'pickup')}
                <label className={[styles.formField, fieldErrors['pickup.date'] && styles.invalid].filter(Boolean).join(' ')}>
                  <span>Date</span>
                  <input
                    aria-label="Pickup date"
                    type="date"
                    value={pickupDate}
                    onChange={(event) => setPickupDate(event.target.value)}
                    onBlur={() => setTouched((prev) => ({ ...prev, 'pickup.date': true }))}
                    required
                  />
                  {shouldShowError('pickup.date') && renderFieldError('pickup.date')}
                </label>
              </div>
              <div className={styles.routeRow}>
                {locationField('delivery', 'Delivery', deliveryLocation, setDeliveryLocation, setDeliverySelectorSignal, 'delivery')}
                <label className={[styles.formField, fieldErrors['delivery.date'] && styles.invalid].filter(Boolean).join(' ')}>
                  <span>Date</span>
                  <input
                    aria-label="Delivery date"
                    type="date"
                    value={deliveryDate}
                    onChange={(event) => setDeliveryDate(event.target.value)}
                    onBlur={() => setTouched((prev) => ({ ...prev, 'delivery.date': true }))}
                    required
                  />
                  {shouldShowError('delivery.date') && renderFieldError('delivery.date')}
                </label>
              </div>
            </div>
          </div>

          <div className={styles.actions}>
            <Button type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting || !isFormValid}>
              {isSubmitting ? 'Creating shipment…' : 'Create shipment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  ) : null;
};

QuickCreateShipmentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default QuickCreateShipmentModal;
